import React, { useEffect, useMemo, useState } from "react";
import OrendaNav from "./OrendaNav";
import OrendaCardReveal from "./OrendaCardReveal";
import OrendaAccountDetails from "./OrendaAccountDetails";
import OrendaBeneficiaries from "./OrendaBeneficiaries";
import OrendaPayments from "./OrendaPayments";
import OrendaTransactions from "./OrendaTransactions";
import Orenda3ds from "./Orenda3ds";

// Orenda cardholder dashboard.
//
// Composition:
//   • Nav rail (left)
//   • Hero band  — the user's card on the left, status + metadata on the
//                  right, side-by-side on a plum-washed surface so the whole
//                  area reads as one object instead of a card floating alone.
//   • Transactions table — full width below. Where the user will live once
//                          there are rows to show.
//   • A disclosed "View card details" drawer holds the long IDs (Card ID,
//     Account ID, Customer ID) that used to live in a permanent right rail.
//     Those are debug-ish fields a user looks at once; giving them a column
//     made the layout feel empty on wide viewports.

const SESSION_FLAG_KEY = "orendaCard";

const readStoredCard = () => {
  try {
    const raw = window.sessionStorage.getItem(SESSION_FLAG_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const buildFallbackCard = (application) => ({
  id: application?.applicationId || "",
  customerId: application?.customerId || "",
  accountId: "",
  cardType: "VIRTUAL",
  status: "ACTIVE",
  provider: "",
});

const formatIssuedDate = (application) => {
  const raw =
    application?.updatedAt ||
    application?.createdDateTime ||
    application?.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const OrendaDashboard = ({ application, cachedCardDetails }) => {
  const [card, setCard] = useState(() => readStoredCard() || buildFallbackCard(application));
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revealOpen, setRevealOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  // Internal view switcher — the nav rail swaps between "dashboard" and
  // "beneficiaries" without a route change so the loaded application state
  // and modal plumbing survive navigation.
  const [activeView, setActiveView] = useState("dashboard");

  useEffect(() => {
    const stored = readStoredCard();
    if (stored) setCard(stored);
  }, []);

  // Paint the whole document while this screen is mounted so the browser's
  // default white margin and any inherited padding on #onboarding / #root
  // don't leak through as a pale strip on the sides.
  useEffect(() => {
    document.body.classList.add("orenda-dash-body");
    return () => document.body.classList.remove("orenda-dash-body");
  }, []);

  const applicant = application?.primaryApplicant || {};
  const fullName = useMemo(
    () => [applicant.firstName, applicant.lastName].filter(Boolean).join(" ").trim(),
    [applicant.firstName, applicant.lastName]
  );
  const firstName = applicant.firstName || "";
  const issued = formatIssuedDate(application);

  return (
    <div className="row minh-row orenda-dash">
      <OrendaNav activePath={activeView} onNavigate={setActiveView} />

      {activeView === "beneficiaries" && (
        <main className="col-md-12 col-lg-9 col-xl-9 orenda-dash-canvas">
          <OrendaBeneficiaries />
        </main>
      )}

      {activeView === "payments" && (
        <main className="col-md-12 col-lg-9 col-xl-9 orenda-dash-canvas">
          <OrendaPayments />
        </main>
      )}

      {activeView === "transactions" && (
        <main className="col-md-12 col-lg-9 col-xl-9 orenda-dash-canvas">
          <OrendaTransactions />
        </main>
      )}

      {activeView === "3ds" && (
        <main className="col-md-12 col-lg-9 col-xl-9 orenda-dash-canvas">
          <Orenda3ds />
        </main>
      )}

      {activeView === "dashboard" && (
      <main className="col-md-12 col-lg-9 col-xl-9 orenda-dash-canvas">
        {/* ── Hero band ───────────────────────────────────────── */}
        <section className="orenda-hero" aria-label="Your card">
          <div className="orenda-hero-inner">
            <div className="orenda-hero-head">
              <div className="orenda-hero-eyebrow">Your card</div>
              <h1 className="orenda-hero-greet">
                Welcome back{firstName ? `, ${firstName}` : ""}.
              </h1>
            </div>

            <div className="orenda-hero-grid">
              <CardArtifact card={card} fullName={fullName} />

              <aside className="orenda-hero-meta">
                <div className="orenda-hero-meta-group">
                  <span className="orenda-meta-label">Status</span>
                  <div className="orenda-meta-status">
                    <span className="orenda-meta-status-dot" aria-hidden />
                    {card.status || "ACTIVE"}
                  </div>
                  <span className="orenda-meta-sub">
                    {card.cardType || "VIRTUAL"} card
                    {card.provider ? ` · ${card.provider}` : ""}
                  </span>
                </div>

                {issued && (
                  <div className="orenda-hero-meta-group">
                    <span className="orenda-meta-label">Issued</span>
                    <div className="orenda-meta-value">{issued}</div>
                  </div>
                )}

                {applicant.email && (
                  <div className="orenda-hero-meta-group">
                    <span className="orenda-meta-label">Cardholder</span>
                    <div className="orenda-meta-value">
                      {fullName || "—"}
                    </div>
                    <span className="orenda-meta-sub">{applicant.email}</span>
                  </div>
                )}

                <div className="orenda-hero-actions">
                  <button
                    type="button"
                    className="orenda-hero-cta"
                    onClick={() => setRevealOpen(true)}
                  >
                    <EyeIcon />
                    Show card number
                  </button>
                  <button
                    type="button"
                    className="orenda-hero-cta orenda-hero-cta--secondary"
                    onClick={() => setAccountOpen(true)}
                  >
                    <BankIcon />
                    Account details
                  </button>
                  <button
                    type="button"
                    className="orenda-hero-link"
                    aria-expanded={detailsOpen}
                    aria-controls="orenda-card-details"
                    onClick={() => setDetailsOpen((o) => !o)}
                  >
                    {detailsOpen ? "Hide" : "View"} card details
                    <Chevron open={detailsOpen} />
                  </button>
                </div>
              </aside>
            </div>

            {/* Collapsible details — the ex-right-rail content, on demand */}
            <div
              id="orenda-card-details"
              className={`orenda-details ${detailsOpen ? "orenda-details--open" : ""}`}
              role="region"
              aria-hidden={!detailsOpen}
            >
              <div className="orenda-details-grid">
                <DetailCell label="Card ID" value={card.id} mono />
                <DetailCell label="Account ID" value={card.accountId} mono />
                <DetailCell label="Customer ID" value={card.customerId} mono />
                <DetailCell label="Type" value={card.cardType || "VIRTUAL"} />
                <DetailCell label="Status" value={card.status || "ACTIVE"} />
                {card.provider && (
                  <DetailCell label="Provider" value={card.provider} />
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── Transactions ────────────────────────────────────── */}
        <section className="orenda-tx-section">
          <div className="orenda-tx-head">
            <h2 className="orenda-section-title">Transactions</h2>
            <div className="orenda-tx-head-right">
              <span className="orenda-tx-period">This month</span>
            </div>
          </div>

          <div className="orenda-tx">
            <div className="orenda-tx-search">
              <SearchIcon />
              <input
                type="search"
                placeholder="Search by merchant, status, or amount"
                disabled
                aria-label="Search transactions"
              />
            </div>

            <div className="orenda-tx-table-wrap">
              <table className="orenda-tx-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Merchant</th>
                    <th>Status</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th style={{ textAlign: "right" }}>Receipt</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={5} className="orenda-tx-empty">
                      <EmptyIllustration />
                      <div className="orenda-tx-empty-title">
                        No transactions yet
                      </div>
                      <div className="orenda-tx-empty-sub">
                        Your purchases will land here the moment you use your card.
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
      )}

      <OrendaCardReveal
        open={revealOpen}
        onClose={() => setRevealOpen(false)}
        cachedCardDetails={cachedCardDetails}
      />

      <OrendaAccountDetails
        open={accountOpen}
        onClose={() => setAccountOpen(false)}
      />
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Card artifact — the "real card" at the heart of the hero
// ─────────────────────────────────────────────────────────────────
const CardArtifact = ({ card, fullName }) => (
  <div className="orenda-cardart" role="img" aria-label="Your virtual card">
    <div className="orenda-cardart-face">
      <div className="orenda-cardart-top">
        <div className="orenda-cardart-brand">
          <span className="orenda-cardart-brand-bo">bo</span>
          <span className="orenda-cardart-brand-p">payments</span>
        </div>
        <ContactlessMark />
      </div>

      <div className="orenda-cardart-mid">
        <div className="orenda-cardart-chip" aria-hidden>
          <span /><span /><span /><span /><span /><span />
        </div>
      </div>

      <div className="orenda-cardart-number">
        •••• •••• •••• ••••
      </div>

      <div className="orenda-cardart-foot">
        <div className="orenda-cardart-foot-l">
          <div className="orenda-cardart-foot-label">Cardholder</div>
          <div className="orenda-cardart-foot-value">
            {fullName || "Verified cardholder"}
          </div>
        </div>
        <div className="orenda-cardart-foot-r">
          <div className="orenda-cardart-foot-label">{card.cardType || "VIRTUAL"}</div>
          <NetworkMark />
        </div>
      </div>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────
// Small bits
// ─────────────────────────────────────────────────────────────────
const DetailCell = ({ label, value, mono }) => (
  <div className="orenda-detail-cell">
    <div className="orenda-detail-label">{label}</div>
    <div
      className={`orenda-detail-value${mono ? " orenda-detail-value--mono" : ""}`}
    >
      {value || "—"}
    </div>
  </div>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// Simple bank/building glyph for the Account details CTA. Matches the
// EyeIcon stroke style so both buttons feel visually consistent.
const BankIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3 10l9-6 9 6" />
    <path d="M5 10v9" />
    <path d="M9 10v9" />
    <path d="M15 10v9" />
    <path d="M19 10v9" />
    <path d="M3 20h18" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const ContactlessMark = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
    <path d="M8.5 7a7 7 0 0 1 0 10" />
    <path d="M12 9.5a4 4 0 0 1 0 5" />
    <circle cx="15.5" cy="12" r="0.5" fill="currentColor" />
  </svg>
);

const NetworkMark = () => (
  // Abstract overlap-circles mark — reads as "network" without miming
  // a real brand we don't have the right to use.
  <svg viewBox="0 0 36 20" width="42" height="24" aria-hidden>
    <circle cx="13" cy="10" r="8" fill="rgba(255,255,255,0.75)" />
    <circle cx="23" cy="10" r="8" fill="rgba(193,166,176,0.65)" style={{ mixBlendMode: "lighten" }} />
  </svg>
);

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const EmptyIllustration = () => (
  <svg viewBox="0 0 120 72" width="120" height="72" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    {/* Receipt silhouette */}
    <path d="M30 6 L30 66 L36 62 L42 66 L48 62 L54 66 L60 62 L66 66 L72 62 L78 66 L84 62 L90 66 L90 6 Z" />
    <line x1="40" y1="22" x2="80" y2="22" strokeDasharray="2 3" />
    <line x1="40" y1="34" x2="72" y2="34" strokeDasharray="2 3" />
    <line x1="40" y1="46" x2="78" y2="46" strokeDasharray="2 3" />
    {/* Soft plum glow underneath */}
    <ellipse cx="60" cy="68" rx="32" ry="2" opacity="0.35" />
  </svg>
);

export default OrendaDashboard;
